import "./SectionSidebarOptionsMenu.scss";

import { FC, Ref, useRef, useState } from "react";

import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "./SidebarOptionsMenu";

import { showAddSectionModal } from "components/materials/home/admin/content/helpers";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { SectionType } from "components/server-types";
import { t } from "i18n/i18n";
import { CancelSaveWidget } from "mds/components/CancelSaveWidget";
import { IconText } from "mds/components/IconText";
import { MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import {
  ArrowDownToLineIcon,
  BookSectionLinkedIcon,
  BookSectionTemplateIcon,
  CopySendArrowIcon,
  EyeNotVisibleIcon,
  EyeVisibleIcon,
  GearSettingsIcon,
  PencilEditIcon,
} from "mds/icons";
import { useIsArchived } from "utils/hooks/use-is-archived";
import { isLinkedSection, isTemplateSection, isUnlinkedSection } from "utils/publishing";

interface SectionSidebarOptionsMenuProps extends SidebarOptionsMenuProps {
  section: SectionType;
  optionsMenuActions: SidebarOptionsMenuProps["optionsMenuActions"] & {
    onSettings?: () => void;
    onRename?: () => void;
    onCopyTo?: (sectionId: string, courseId?: string, termId?: string) => void;
    onMoveTo?: (courseId: string, termId: string) => void;
    onExport?: () => void;
    onArchive?: () => void;
    onUnarchive?: () => void;
    onDuplicateAsTemplate?: () => void;
  };
}

export const SectionSidebarOptionsMenu: FC<SectionSidebarOptionsMenuProps> = ({
  styles,
  className,
  direction = "bottom",
  section,
  optionsMenuActions,
}) => {
  const sectionSidebarMenuRef: Ref<MenuInstance> = useRef(null);
  const [selectedCourseId, setSelectedCourseId] = useState(section.course_id);
  const [selectedTermId, setSelectedTermId] = useState(section.term_id);
  const { canUnarchive, isArchived } = useIsArchived();
  const isSectionArchived = isArchived(section);
  const unarchiveDisabled = !canUnarchive(section);

  // NOTE: We use the same state for both copy and move to, this should be fine as these menus will never
  // render at the same time
  const handleCourseChange = (newCourseId: string) => {
    setSelectedCourseId(newCourseId);
  };

  const handleTermChange = (newTermId: string) => {
    setSelectedTermId(newTermId);
  };

  const copyToMenuItem = optionsMenuActions.onCopyTo && !isLinkedSection(section) && (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.copy_to")} />
      }
      withArrow
    >
      <div className="h4 m-2 min-w-[280px]">
        <div className="pb-2 text-black-tint-40">{t("common.copy_to")}</div>

        <div className="flex">
          <CourseMenu
            defaultToAll={false}
            selectedCourseId={selectedCourseId}
            onCourseChange={handleCourseChange}
          />

          <TermMenu
            defaultToAll={false}
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </div>

        <CancelSaveWidget
          className="right pt-2"
          saveTextKey="copy"
          onClickCancel={() => {
            sectionSidebarMenuRef.current?.closeMenu();
          }}
          onClickSave={() =>
            optionsMenuActions.onCopyTo?.(section.id, selectedCourseId, selectedTermId)
          }
        />
      </div>
    </SubMenu>
  );

  const moveToMenuItem = optionsMenuActions.onMoveTo && isUnlinkedSection(section) && (
    <SubMenu
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_section")} />
      }
      withArrow
    >
      <div className="h4 m-2 min-w-[280px]">
        <div className="pb-2 text-black-tint-40">{t("sidebar_options_menu.move_section_to")}</div>

        <div className="flex">
          <CourseMenu
            defaultToAll={false}
            selectedCourseId={selectedCourseId}
            onCourseChange={handleCourseChange}
          />

          <TermMenu
            defaultToAll={false}
            selectedTermId={selectedTermId}
            onTermChange={handleTermChange}
          />
        </div>

        <CancelSaveWidget
          className="right pt-2"
          saveTextKey="move"
          onClickCancel={() => {
            sectionSidebarMenuRef.current?.closeMenu();
          }}
          onClickSave={() => {
            optionsMenuActions.onMoveTo?.(selectedCourseId, selectedTermId);
            sectionSidebarMenuRef.current?.closeMenu();
          }}
        />
      </div>
    </SubMenu>
  );

  const sectionSettingItem = optionsMenuActions.onSettings && (
    <MenuItem
      eventName="Admin Courses - Section Settings - Open"
      title={t("settings.section_settings")}
      onClick={optionsMenuActions.onSettings}
    >
      <IconText iconStart={<GearSettingsIcon />} text={t("settings.section_settings")} />
    </MenuItem>
  );

  const addLinkedSectionMenuItem = isTemplateSection(section) && !isSectionArchived && (
    <MenuItem
      onClick={() =>
        showAddSectionModal({
          defaultCourseId: section.course_id,
          defaultTermId: section.term_id,
          defaultTemplateId: section.id,
        })
      }
    >
      <IconText
        iconStart={<BookSectionLinkedIcon />}
        text={t("sidebar_options_menu.add_linked_section")}
      />
    </MenuItem>
  );

  const renameMenuItem = optionsMenuActions.onRename && (
    <MenuItem onClick={optionsMenuActions.onRename}>
      <IconText iconStart={<PencilEditIcon />} text={t("sidebar_options_menu.rename_section")} />
    </MenuItem>
  );

  const exportMenuItem = optionsMenuActions.onExport && (
    <MenuItem onClick={optionsMenuActions.onExport}>
      <IconText iconStart={<ArrowDownToLineIcon />} text={t("common.export")} />
    </MenuItem>
  );

  const archiveMenuItem = !isSectionArchived && optionsMenuActions.onArchive && (
    <MenuItem onClick={optionsMenuActions.onArchive}>
      <IconText iconStart={<EyeNotVisibleIcon />} text={t("sidebar_options_menu.archive")} />
    </MenuItem>
  );

  const unarchiveMenuItem = isSectionArchived && optionsMenuActions.onUnarchive && (
    <MenuItem disabled={unarchiveDisabled} onClick={optionsMenuActions.onUnarchive}>
      <IconText iconStart={<EyeVisibleIcon />} text={t("sidebar_options_menu.unarchive")} />
    </MenuItem>
  );

  const duplicateAsTemplateMenuItem = optionsMenuActions.onDuplicateAsTemplate &&
    isUnlinkedSection(section) && (
      <MenuItem onClick={optionsMenuActions.onDuplicateAsTemplate}>
        <IconText
          iconStart={<BookSectionTemplateIcon />}
          text={t("sidebar_options_menu.duplicate_as_template")}
        />
      </MenuItem>
    );

  return (
    <SidebarOptionsMenu
      className={className}
      direction={direction}
      instanceRef={sectionSidebarMenuRef}
      optionsMenuActions={optionsMenuActions}
      styles={styles}
    >
      {sectionSettingItem}
      {addLinkedSectionMenuItem}
      {renameMenuItem}
      {copyToMenuItem}
      {moveToMenuItem}
      {exportMenuItem}
      {archiveMenuItem}
      {unarchiveMenuItem}
      {duplicateAsTemplateMenuItem}
    </SidebarOptionsMenu>
  );
};
